import * as Menu from '@radix-ui/react-dropdown-menu';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { useNavigate } from 'react-router';
import type { Me } from '@/api/session';
import type { DepartmentNames } from '@/app/departments';
import { CONTROL_ROOM_NAME, homeOf, type Scope } from '@/app/nav';
import { useScope } from '@/app/scope';

/**
 * Menus and the department switch (FRONTEND-AUDIT §D, B9): one surface and
 * one row for every dropdown, promoted from the `MENU` and `row` strings
 * in `app/TopBar.tsx`, and the switcher promoted from the rail's foot so a
 * phone sheet can reuse it.
 */

const SURFACE = 'z-40 flex flex-col border border-strong bg-page shadow-toast animate-[nrise_160ms_ease-brand_both] outline-none';
const ROW = 'flex min-h-[46px] cursor-pointer items-center gap-2.5 border-b border-line px-4 text-dense text-secondary outline-none data-[highlighted]:bg-raised data-[highlighted]:text-ink';

/** A dropdown's surface, portalled: ink rule, the toast's shadow, and a width the caller gives. */
export function MenuContent({ className = '', children, ...rest }: ComponentPropsWithoutRef<typeof Menu.Content> & { children: ReactNode }) {
  return (
    <Menu.Portal>
      <Menu.Content align="end" sideOffset={6} {...rest} className={`${SURFACE} ${className}`}>
        {children}
      </Menu.Content>
    </Menu.Portal>
  );
}

/** One row of a menu, 46px tall. `last` drops the rule under the final row. */
export function MenuItem({ className = '', last, ...rest }: ComponentPropsWithoutRef<typeof Menu.Item> & { last?: boolean }) {
  return <Menu.Item {...rest} className={`${ROW} ${last ? 'border-b-0' : ''} ${className}`} />;
}

export { Menu };

/**
 * What a department is to this person, in the prototype's words and colours
 * (P:56-64): moss where an agent is working, yellow for a pilot, grey for
 * one that only reads, red for one that is stopped, ink otherwise.
 */
export function scopeOf(name: string, me: Me, directory: DepartmentNames): { mark: string; kind: string } {
  const info = directory.info(name);
  const readOnly = directory.readOnly(name);
  const parts: string[] = [];
  if (me.leads.includes(name)) parts.push('You lead it');
  else if (info?.lead) parts.push(`Lead ${info.lead.displayName.split(' ')[0]}`);
  if (info?.status === 'pilot') parts.push('pilot');
  if (info?.status === 'suspended') parts.push('stopped');
  if (readOnly) parts.push('read only');
  if (parts.length === 0) parts.push(me.isSuperadmin ? 'You oversee it · superadmin' : 'A member');
  const mark =
    info?.status === 'suspended'
      ? 'var(--danger)'
      : readOnly
        ? 'var(--border-control)'
        : info?.status === 'pilot'
          ? 'var(--warning)'
          : (info?.agents.live ?? 0) > 0
            ? 'var(--accent)'
            : 'var(--text-primary)';
  return { mark, kind: parts.join(' · ') };
}

/**
 * Which department this person is working in, and the others they may
 * switch to, each with what it is to them (P:56-64). Opened and closed by
 * its caller, which also knows what counts as clicking away. On a phone
 * (`sheet`) the list rises from the foot of the screen over a scrim, as
 * every phone sheet does, instead of opening above the button (B9).
 */
export function ScopeSwitcher({
  me,
  directory,
  open,
  onOpenChange,
  sheet = false,
}: {
  me: Me;
  directory: DepartmentNames;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sheet?: boolean;
}) {
  const { scope, options, pick, switching, failed } = useScope(me);
  const navigate = useNavigate();
  const labelOf = (s: Scope) => (s.kind === 'control' ? CONTROL_ROOM_NAME : directory.label(s.name));
  const same = (a: Scope, b: Scope) => a.kind === b.kind && (a.kind === 'control' || b.kind === 'control' || a.name === (b as { name: string }).name);
  return (
    <>
      {open && sheet && (
        <button type="button" aria-label="Close departments" className="fixed inset-0 z-50 bg-[var(--scrim)]" onClick={() => onOpenChange(false)} />
      )}
      {open && (
        <div
          role="listbox"
          aria-label="Departments"
          className={`flex animate-[nrise_160ms_ease-brand_both] flex-col border border-strong bg-page ${
            sheet
              ? 'fixed inset-x-0 bottom-0 z-50 max-h-[70dvh] overflow-y-auto border-x-0 border-b-0 pb-[env(safe-area-inset-bottom)]'
              : 'absolute bottom-full left-3 right-3 z-10 mb-1.5'
          }`}
        >
          {sheet && <span className="px-4 pb-1 pt-4 text-micro uppercase tracking-label text-meta">Work in</span>}
          {options.map((s) => {
            const view = s.kind === 'control' ? { mark: 'var(--text-primary)', kind: 'Company-wide oversight · superadmin' } : scopeOf(s.name, me, directory);
            const id = s.kind === 'control' ? 'control-room' : s.name;
            return (
              <button
                key={id}
                type="button"
                role="option"
                aria-selected={same(s, scope)}
                data-department={id}
                disabled={switching}
                onClick={() => {
                  onOpenChange(false);
                  // A scope opens on its own home, so no page of the one
                  // left behind is read under the new one's name.
                  if (!same(s, scope)) pick(s, () => void navigate(homeOf(s)));
                }}
                className={`flex min-h-tap flex-col gap-0.5 border-b border-line px-3 py-[11px] text-left last:border-b-0 hover:bg-sunken ${
                  same(s, scope) ? 'bg-raised' : ''
                }`}
              >
                <span className="flex items-center gap-2">
                  <span aria-hidden className="block h-2 w-2" style={{ background: view.mark }} />
                  <span className="text-dense font-semibold">{labelOf(s)}</span>
                </span>
                <span className="pl-4 text-label text-meta">{view.kind}</span>
              </button>
            );
          })}
        </div>
      )}
      <button
        type="button"
        aria-label={`Department: ${labelOf(scope)}. Switch`}
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
        className="flex min-h-[52px] flex-col gap-0.5 border border-control bg-page px-3 py-2.5 text-left hover:border-strong"
      >
        <span className="text-micro uppercase tracking-label text-meta">Working in</span>
        <span className="flex items-center justify-between gap-2">
          <span className="text-dense font-semibold">{labelOf(scope)}</span>
          <span aria-hidden className="text-label text-meta">
            ▾
          </span>
        </span>
      </button>
      {failed && <span className="px-1 text-dense text-danger">That is not one of your departments.</span>}
    </>
  );
}
